import { publicApi } from ".";
import { Account } from "../../models/Account";
import { User } from "../../models/User";
import { reCaptcha } from "../../services/reCaptcha";

const baseUrl = "/accounts";

export const accountApi = publicApi.injectEndpoints({
  endpoints: (builder) => ({
    confirmAccount: builder.mutation<void, Account>({
      query: (arg) => ({ url: baseUrl, method: "POST", data: arg }),
    }),
    confirmEmail: builder.mutation<
      Account,
      { code: string; secret: string; type: "code" | "email" }
    >({
      queryFn: async (arg, _queryLifecycleApi, _extraOptions, fetch) => {
        const reCaptchaToken = await reCaptcha.execute("verify");
        const response = await fetch({
          url: `${baseUrl}/verify`,
          method: "POST",
          data: { ...arg, reCaptchaToken },
        });

        if (response.error) {
          return { error: response.error };
        }

        return { data: response.data as Account };
      },
    }),
    confirmSignUp: builder.query<void, { code: string; username: string }>({
      query: (arg) => ({
        url: `${baseUrl}/confirm`,
        method: "POST",
        data: arg,
      }),
    }),
    forgotPasswordSendEmail: builder.mutation<null, Pick<User, "email">>({
      queryFn: async (arg, _queryLifecycleApi, _extraOptions, fetch) => {
        const reCaptchaToken = await reCaptcha.execute(
          "forgotPasswordSendEmail",
        );
        const response = await fetch({
          url: `${baseUrl}/forgot-password`,
          method: "POST",
          data: { email: arg.email.toLowerCase(), reCaptchaToken },
        });

        if (response.error) {
          return { error: response.error };
        }

        return { data: null };
      },
    }),
    forgotPasswordSubmit: builder.mutation<
      null,
      { code: string; password: string; username: string }
    >({
      queryFn: async (arg, _queryLifecycleApi, _extraOptions, fetch) => {
        const reCaptchaToken = await reCaptcha.execute("forgotPasswordSubmit");
        const response = await fetch({
          url: `${baseUrl}/forgot-password-confirm`,
          method: "POST",
          data: { ...arg, reCaptchaToken },
        });

        if (response.error) {
          return { error: response.error };
        }

        return { data: null };
      },
    }),
    sendActivationEmail: builder.mutation<
      void,
      { email?: string; username?: string }
    >({
      query: (arg) => ({
        url: `${baseUrl}/activation-email`,
        method: "POST",
        data: arg,
      }),
    }),
    sendConfirmEmail: builder.mutation<
      null,
      { code: string; type: "code" | "email" }
    >({
      queryFn: async (arg, _queryLifecycleApi, _extraOptions, fetch) => {
        const reCaptchaToken = await reCaptcha.execute("activate");
        const response = await fetch({
          url: `${baseUrl}/activate`,
          method: "POST",
          data: { ...arg, reCaptchaToken },
        });

        if (response.error) {
          return { error: response.error };
        }

        return { data: null };
      },
    }),
  }),
});

export const {
  useConfirmAccountMutation,
  useConfirmEmailMutation,
  useConfirmSignUpQuery,
  useForgotPasswordSendEmailMutation,
  useForgotPasswordSubmitMutation,
  useSendActivationEmailMutation,
  useSendConfirmEmailMutation,
} = accountApi;
